import { UploadOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Input, Modal, Progress, Spin, Tag, Upload } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import FormItem from 'antd/es/form/FormItem';
import { UploadFile } from 'antd/es/upload/interface';
import { RcFile } from 'antd/lib/upload';
import { AccountBean, FilterListBean, FilterListFileBean } from 'common/api/api';
import { directApi, filterListApi } from 'common/api/entitiesApi';
import { AnchorsForm } from 'common/components/AnchorsForm';
import { ButtonsPanel } from 'common/components/ButtonsPanel';
import { CardTitleWithTooltip } from 'common/components/CardTitleWithTooltip'
import { FilterListTypeSelector } from 'common/components/dictionary/FilterListTypeSelector';
import { OwnedBySelector } from 'common/components/dictionary/OwnedBySelector';
import { FormCard } from 'common/components/FormCard';
import { AdvertiserSelector } from 'common/components/reloadable/AdvertiserSelector';
import { useAppContext } from 'common/context/AppContext';
import { CommonRoutes } from 'common/context/commonRoutes.ts';
import { useUserContext } from 'common/context/UserContext.tsx';
import { queueListRepositionOnModifiedChange } from 'common/util/listNavigation.ts';
import { apiCall, FormMode, getCopyName, showError } from 'common/util/Util'
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ReadNextLine } from 'read-next-line';
import validator from 'validator';
import RoleEnum = AccountBean.RoleEnum

const {TextArea} = Input
const Type = FilterListBean.TypeEnum
const Advertiser = RoleEnum.Advertiser.toString()
const Admin = RoleEnum.Admin.toString()

export const FilterListForm = ({mode, admin, agency}) => {
    const {id} = useParams<{ id?: string }>()
    const idNum = id ? parseInt(id) : 0
    const [loading, setLoading] = useState(false)
    const [form] = Form.useForm();
    const [entity, setEntity] = useState<FilterListBean>({})
    const [uploadProgress, setUploadProgress] = useState(0)
    const [currentUploadXHR, setCurrentUploadXHR] = useState<XMLHttpRequest | undefined>()
    const [uploading, setUploading] = useState(false)
    const navigateTo = useNavigate()
    const location = useLocation()
    const appContext = useAppContext()
    const type = useWatch('type', form)
    const [uploadedFiles, setUploadedFiles] = useState<UploadFile[] | undefined>([])
    const ownerType = Form.useWatch<string>('ownerType', form)
    const { user } = useUserContext()
    const returnRoute = (location.state as { returnTo?: string } | null)?.returnTo || CommonRoutes.FILTER_LIST

    useEffect(() => {
        if (mode === 'edit' || mode === 'copy') {
            apiCall(setLoading, appContext,
                () => filterListApi.getFilterList(idNum),
                (data) => {
                    const entityData = mode === 'copy' ? {...data, name: getCopyName(data.name)} : data
                    setEntity(entityData)
                    form.setFieldsValue({...entityData})
                    form.setFieldValue('ownerType', data.advertiserId ? Advertiser : Admin)
                    setUploadedFiles(data.files && data.files.map(file => ({
                        uid: file.uid,
                        name: file.name,
                        size: file.size
                    } as UploadFile)))
                }
            )
        }
    }, [mode, id]);

    useEffect(() => {
        if (ownerType !== Advertiser) {
            form.setFieldValue('advertiserId', undefined)
        }
    }, [ownerType]);

    const saveFilterList = () => {
        form.validateFields().then(formValues => {
            apiCall(setLoading, appContext,
                () => {
                    // keep only files which are not deleted
                    const oldFiles = entity.files?.filter(file => findExistedFile(file.uid, uploadedFiles))
                    // keep new files
                    const newUploadFiles = uploadedFiles?.filter(uploadFile => !findExistedFile(uploadFile.uid, entity.files))
                    const newFiles: Array<FilterListFileBean> = oldFiles || [] as FilterListFileBean[]
                    (newUploadFiles || []).forEach(uploadFile => newFiles.push({
                        uid: uploadFile.uid,
                        name: uploadFile.name,
                        size: uploadFile.size
                    } as FilterListFileBean))
                    if (!admin && !agency && user?.advertiserId) formValues.advertiserId = user.advertiserId
                    const newEntity = {...entity, ...formValues}
                    newEntity.files = newFiles
                    return mode === FormMode.create ?
                        filterListApi.createFilterList(newEntity) :
                        filterListApi.updateFilterList(newEntity, idNum)
                },
                (response) => {
                    queueListRepositionOnModifiedChange({
                        appContext,
                        routeKey: CommonRoutes.FILTER_LIST,
                        previousEntity: entity,
                        responseEntity: response,
                        enabled: mode === FormMode.edit && returnRoute === CommonRoutes.FILTER_LIST
                    })

                    navigateTo(returnRoute)
                }
            )
        })
    }

    const findExistedFile = (uid: string | undefined, files: Array<any> | undefined) => {
        return uid && files && files.find(file => file.uid === uid)
    }

    const deleteFilterList = () => {
        apiCall(setLoading, appContext,
            () => filterListApi.deleteFilterList(idNum),
            () => navigateTo(returnRoute)
        )
    }

    async function checkFileContent(file: RcFile, lines: number, type) {
        const reader = new ReadNextLine(file.stream());
        try {
            let lineCount = 0
            let line
            while (lineCount < lines) {
                lineCount += 1
                line = await reader.readLine();
                if (line !== null) {
                    const result = checkLineContent(line, type)
                    if (result) {
                        return `Line ${lineCount}: ${result}`
                    }
                } else {
                    break
                }
            }
        } catch (e) {
            return e
        } finally {
            reader.release();
        }
        return undefined
    }

    function checkLineContent(line: string, type) {
        if (type == Type.USERIDIFA) {
            if (!validator.isUUID(line)) {
                return `${line} isn't UUID`
            }
        }
        return undefined
    }

    const showSize = ({size = 0}) => (
        <span style={{
            color: '#cccccc',
            marginLeft: 10
        }}>({(size / 1024 / 1024).toFixed(2)}MB)</span>
    )

    function uploadFile(file: UploadFile, fileList: UploadFile[]) {
        const fileWithOriginObj = fileList.filter(elem => elem.uid === file.uid)[0]
        if (fileWithOriginObj.originFileObj) {
            setUploading(true)
            directApi.uploadFilterListFile(
                type,
                fileWithOriginObj.originFileObj,
                progress => setUploadProgress(progress),
                xhr => setCurrentUploadXHR(xhr)
            )
                .then((id: string) => {
                    fileWithOriginObj.uid = id // save UID assigned by backend
                    setUploadedFiles(fileList)
                })
                .catch(e => {
                    if (e instanceof ProgressEvent) {
                        showError(appContext, "Can't upload the file. Check the network connectivity and/or file size.")
                    } else {
                        showError(appContext, e)
                    }
                })
                .finally(() => setUploading(false))
        }
    }


    return (
        <Spin spinning={loading}>
            <AnchorsForm form={form} onFinish={saveFilterList}>
                <Modal title="Uploading file" open={uploading} footer={[
                    <Button key="cancel" onClick={() => {
                        currentUploadXHR && currentUploadXHR.abort()
                        setUploading(false)
                    }
                    }>
                        Cancel
                    </Button>
                ]}>
                    <Progress percent={uploadProgress}/>
                </Modal>

                <FormCard
                    title={
                        <CardTitleWithTooltip
                            title={
                                <>
                                    Filter List
                                <Tag
                                    color={entity.processed ? 'green' : '#E6E6E6'}
                                    style={{
                                      marginLeft: '14px',
                                      height: '24px',
                                      color: '#343D48',
                                    }}
                                >
                                    {entity.processed ? 'Processed' : 'Pending'}
                                </Tag>
                                </>
                            }
                            tooltip="A collection of rules used to block or allow specific inventory, content, or traffic sources"
                        />
                    }
                >
                    {(agency) && (<FormItem label="Owner Type" tooltip="Specifies who owns or manages the filter list (e.g., advertiser, agency)." name="ownerType"
                                      rules={[{required: true, message: 'Owner type is required'}]}
                            >
                                <OwnedBySelector style={{width: 200}} disabled={mode === 'edit'}/>
                            </FormItem>
                        )}
                    {ownerType === Advertiser && <FormItem
                                label="Advertiser"
                                name="advertiserId"
                                rules={[{ required: true, message: 'Advertiser is required' }]}
                            >
                                <AdvertiserSelector disabled={mode === 'edit' || ownerType !== Advertiser} placeholder="Select advertiser" />
                            </FormItem>
                    }

                    <FormItem required name="name" label="Name"
                              rules={[{required: true, message: 'Please input name'}]}>
                        <Input/>
                    </FormItem>

                    <FormItem
                              required
                              name="type"
                              label="Type"
                              tooltip={
                                  <>
                                      <div>Select the type of entity you want to filter by:</div>
                                      <div style={{ marginTop: 8 }}>App/Bundle ID Filter by specific mobile application bundle IDs (e.g., com.example.app).</div>
                                      <div style={{ marginTop: 8 }}>Domain Block or allow traffic from specific website domains.</div>
                                      <div style={{ marginTop: 8 }}>IP v4 Filter by IPv4 addresses or ranges.</div>
                                      <div style={{ marginTop: 8 }}>IP v6 Filter by IPv6 addresses or ranges.</div>
                                      <div style={{ marginTop: 8 }}>User ID (IDFA/AAID) Filter by mobile advertising IDs — IDFA for iOS or AAID for Android.</div>
                                      <div style={{ marginTop: 8 }}>User ID (IP+UA hash) Filter by a hashed combination of IP address and User Agent for device identification.</div>
                                  </>
                              }
                              rules={[{required: true, message: 'Please input type'}]}>
                        <FilterListTypeSelector style={{width: 200}}/>
                    </FormItem>
                    <Form.Item label="Files" tooltip="Upload a file containing multiple filter entries (e.g., CSV, TXT).">
                        <Upload
                            listType="picture"
                            multiple
                            beforeUpload={(file) => {
                                return checkFileContent(file, 10, type).then(contentError => {
                                    if (contentError) {
                                        showError(appContext, contentError)
                                        return Upload.LIST_IGNORE
                                    }
                                    return false
                                })
                            }}
                            showUploadList={{
                                extra: showSize
                            }}
                            fileList={uploadedFiles}
                            onChange={file => {
                                if (file.file.status)
                                    setUploadedFiles(file.fileList)
                                else
                                    uploadFile(file.file, file.fileList)
                            }}
                        >
                            <Flex gap="middle" justify="start" align="flex-start">
                                <Button type="primary" icon={<UploadOutlined/>} disabled={!type}>
                                    Upload
                                </Button>
                            </Flex>
                        </Upload>
                    </Form.Item>
                    <FormItem name="data" label="Additional Records" tooltip="Add more entries or rules to extend the filter list beyond the current items.">
                        <TextArea autoSize={{minRows: 10, maxRows: 18}}/>
                    </FormItem>
                </FormCard>
                <ButtonsPanel
                    onCancel={() => navigateTo(returnRoute)}
                    showDelete={mode == FormMode.edit}
                    onDelete={() => deleteFilterList()}>
                </ButtonsPanel>
            </AnchorsForm>
        </Spin>
    )
}
